import { useState } from 'react';
import { FaTimes, FaCheckCircle, FaTimesCircle, FaShoppingCart, FaMapMarkerAlt, FaTag } from 'react-icons/fa';
import API_URL from '../utils/api';
import { formatDisplayPrice } from '../utils/formatPrice';

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PaymentModal = ({ isOpen, onClose, property }) => {
  const [step, setStep] = useState('confirm'); // confirm | processing | success | failed
  const [paymentId, setPaymentId] = useState('');
  const [error, setError] = useState('');
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' });
  const [formError, setFormError] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');

  if (!isOpen || !property) return null;

  // Parse any price string to a plain number in rupees.
  // Handles:
  //   "23,00,000"          → 2300000  (comma-separated plain number)
  //   "22 Lakhs 2 Thousand"→ 2202000  (human-readable display format)
  //   "1 Cr 50 Lakhs"      → 15000000
  //   "50 Thousand"        → 50000
  //   "2300000"            → 2300000  (plain numeric string)
  const parsePrice = (val) => {
    if (val == null) return 0;
    const s = String(val).trim();

    // If it contains Cr / Lakhs / Thousand — parse human-readable format
    if (/Cr|Lakh|Thousand/i.test(s)) {
      let total = 0;
      const crMatch = s.match(/([\d.]+)\s*Cr/i);
      const lakhMatch = s.match(/([\d.]+)\s*Lakh/i);
      const thousandMatch = s.match(/([\d.]+)\s*Thousand/i);
      if (crMatch)      total += parseFloat(crMatch[1])      * 10000000;
      if (lakhMatch)    total += parseFloat(lakhMatch[1])    * 100000;
      if (thousandMatch)total += parseFloat(thousandMatch[1]) * 1000;
      return Math.round(total) || 0;
    }

    // Plain number — strip commas, ₹, spaces
    const cleaned = s.replace(/[₹,\s]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const maxPrice = parsePrice(property.price || property.expectedPrice);

  // Price is 0 or not set — show a friendly message instead of broken payment
  if (maxPrice <= 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-500 p-5 flex justify-between items-center">
            <div className="flex items-center gap-2 text-white">
              <FaShoppingCart size={18} />
              <h2 className="text-lg font-bold">Buy Property</h2>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition">
              <FaTimes size={20} />
            </button>
          </div>
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaTag className="text-orange-500" size={28} />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Price Not Available</h3>
            <p className="text-gray-500 text-sm mb-6">
              The price for <strong>{property.title}</strong> has not been set yet.
              Please contact us to get the current price and proceed with the purchase.
            </p>
            <button onClick={onClose}
              className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormError('');
    setBuyerInfo({ ...buyerInfo, [e.target.name]: e.target.value });
  };

  const handlePay = async () => {
    if (!buyerInfo.name.trim()) return setFormError('Please enter your full name');
    if (!buyerInfo.email.trim() || !/\S+@\S+\.\S+/.test(buyerInfo.email)) return setFormError('Please enter a valid email');
    if (!/^[0-9]{10}$/.test(buyerInfo.phone)) return setFormError('Please enter a valid 10-digit phone number');
    
    const amountToPay = paymentAmount ? parseFloat(paymentAmount) : maxPrice;
    if (!amountToPay || amountToPay <= 0) return setFormError('Please enter a valid payment amount');
    if (amountToPay > maxPrice) return setFormError(`Amount cannot exceed property price ${formatDisplayPrice(maxPrice)}`);

    setFormError('');
    setStep('processing');

    const loaded = await loadRazorpay();
    if (!loaded) {
      setError('Failed to load payment gateway. Please check your internet connection.');
      setStep('failed');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amountToPay,
          propertyId: property._id,
          propertyTitle: property.title,
          propertyCode: property.propertyCode,
        }),
      });

      const order = await res.json();
      if (!res.ok) throw new Error(order.message || order.error?.description || 'Order creation failed');

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Tanavi Properties',
        description: `Purchase: ${property.title}`,
        order_id: order.orderId,
        prefill: {
          name: buyerInfo.name,
          email: buyerInfo.email,
          contact: buyerInfo.phone,
        },
        theme: { color: '#4f46e5' },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API_URL}/api/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                propertyId: property._id,
                buyerName: buyerInfo.name,
                buyerEmail: buyerInfo.email,
                buyerPhone: buyerInfo.phone,
                amountPaid: amountToPay,
              }),
            });
            const result = await verifyRes.json();
            if (result.success) {
              setPaymentId(response.razorpay_payment_id);
              setStep('success');
            } else {
              setError(result.message || 'Payment signature verification failed. Please contact support.');
              setStep('failed');
            }
          } catch {
            setError('Could not verify payment. Please contact support with your payment ID.');
            setStep('failed');
          }
        },
        modal: {
          ondismiss: () => setStep('confirm'),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(response.error?.description || 'Payment failed. Please try again.');
        setStep('failed');
      });
      rzp.open();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setStep('failed');
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setBuyerInfo({ name: '', email: '', phone: '' });
    setPaymentAmount('');
    setError('');
    setFormError('');
    setPaymentId('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 p-5 flex justify-between items-center">
          <div className="flex items-center gap-2 text-white">
            <FaShoppingCart size={18} />
            <h2 className="text-lg font-bold">Buy Property</h2>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Property Summary */}
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 bg-gray-50">
          <p className="font-bold text-gray-900 text-base leading-snug">{property.title}</p>
          <div className="flex flex-wrap gap-3 mt-2">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <FaMapMarkerAlt className="text-primary" size={11} />
              {property.location}
            </span>
            {property.propertyCode && (
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <FaTag className="text-primary" size={11} />
                ID: {property.propertyCode}
              </span>
            )}
          </div>
            <div className="mt-3">
            <p className="text-xs text-gray-500 mb-1">Total Property Price</p>
            <p className="text-2xl font-extrabold text-gray-700">
              {formatDisplayPrice(maxPrice)}
            </p>
          </div>
        </div>

        <div className="p-5">

          {/* Step: Confirm / Fill Details */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Enter your details and payment amount to proceed</p>

              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount to Pay <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder={`Max: ${formatDisplayPrice(maxPrice)}`}
                    className="w-full pl-8 pr-4 py-2.5 border-2 border-green-300 rounded-lg text-sm font-semibold focus:outline-none focus:border-green-500 bg-green-50"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter booking amount or full price (up to {formatDisplayPrice(maxPrice)})
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={buyerInfo.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={buyerInfo.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  value={buyerInfo.phone}
                  onChange={handleChange}
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-500"
                />
              </div>

              {paymentAmount && parseFloat(paymentAmount) > 0 && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-600 mb-1">You will pay</p>
                  <p className="text-2xl font-extrabold text-green-600">
                    {formatDisplayPrice(parseFloat(paymentAmount))}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePay}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition shadow-md flex items-center justify-center gap-2"
                >
                  <FaShoppingCart size={14} />
                  Proceed to Pay
                </button>
              </div>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="py-10 text-center">
              <div className="w-14 h-14 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-700 font-semibold">Opening payment gateway...</p>
              <p className="text-gray-400 text-sm mt-1">Please do not close this window</p>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="py-8 text-center">
              <FaCheckCircle className="text-green-500 mx-auto mb-4" size={56} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-500 text-sm mb-3">
                Thank you for your purchase. Our team will contact you shortly to complete the process.
              </p>
              {paymentId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-xs text-gray-500 mb-4 break-all">
                  Payment ID: <span className="font-mono font-semibold text-gray-700">{paymentId}</span>
                </div>
              )}
              <button
                onClick={handleClose}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition"
              >
                Done
              </button>
            </div>
          )}

          {/* Step: Failed */}
          {step === 'failed' && (
            <div className="py-8 text-center">
              <FaTimesCircle className="text-red-500 mx-auto mb-4" size={56} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h3>
              <p className="text-red-500 text-sm mb-5">{error || 'Something went wrong. Please try again.'}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setError(''); setStep('confirm'); }}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
